import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { Plus, Trash2 } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { useSnackbar } from "notistack";
import { Chip, Select, Table, TextField } from "../ds";
import type { DataTableColumn } from "../ds/Table";
import {
  createCustodian,
  deleteCustodian,
  listCustodians,
  updateCustodian,
} from "../api/admin";
import { useDepartmentNames } from "../hooks/useDepartments";
import { apiErrorMessage } from "../utils/apiError";
import { buildDepartmentSelectOptions } from "../utils/departments";
import type { Custodian } from "../types/admin";
import { getEssRolesFull } from "../api/auth";

// The admin custodian API stores role as one of two opaque tokens, but
// the human-readable labels live in the yaml. Mapping admin token →
// yaml role name lets us pull the label from /ess/auth/roles instead of
// hardcoding "HR custodian" / "Corporate recipient" here. If the yaml
// rename the underlying role, the dropdown updates with no code change.
const ADMIN_ROLE_TO_YAML_ROLE: Record<"hr" | "corporate", string> = {
  hr: "hr",
  corporate: "corporate_communication",
};

const ADMIN_ROLE_FALLBACK_LABEL: Record<"hr" | "corporate", string> = {
  hr: "HR custodian",
  corporate: "Corporate recipient",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AdminCustodians() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: custodians = [], isLoading } = useQuery({
    queryKey: ["admin-custodians"],
    queryFn: listCustodians,
  });
  const { departments: departmentList } = useDepartmentNames();

  // /ess/auth/roles is the source of truth for role labels. We hold the
  // response for both the dropdown options below and the table chip
  // renderer so neither has to repeat the lookup.
  const { data: rolesResp } = useQuery({
    queryKey: ["ess-roles-full"],
    queryFn: getEssRolesFull,
    staleTime: 5 * 60_000,
  });

  const labelForAdminRole = (adminRole: "hr" | "corporate"): string => {
    const yamlRole = ADMIN_ROLE_TO_YAML_ROLE[adminRole];
    const def = rolesResp?.roles.find((r) => r.role === yamlRole);
    return def?.label ?? ADMIN_ROLE_FALLBACK_LABEL[adminRole];
  };

  const roleOptions = (Object.keys(ADMIN_ROLE_TO_YAML_ROLE) as Array<
    "hr" | "corporate"
  >).map((key) => ({ value: key, label: labelForAdminRole(key) }));

  const [form, setForm] = useState({
    name: "",
    email: "",
    department: "",
    role: "hr" as "hr" | "corporate",
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin-custodians"] });

  const addMutation = useMutation({
    mutationFn: () =>
      createCustodian({
        name: form.name.trim(),
        email: form.email.trim(),
        department: form.department || null,
        role: form.role,
      }),
    onSuccess: () => {
      setForm({ name: "", email: "", department: "", role: "hr" });
      enqueueSnackbar("Custodian added", { variant: "success" });
      invalidate();
    },
    onError: (err) =>
      enqueueSnackbar(apiErrorMessage(err, "Add failed"), {
        variant: "error",
      }),
  });

  const activeMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      updateCustodian(id, { active }),
    onSuccess: () => invalidate(),
    onError: (err) =>
      enqueueSnackbar(apiErrorMessage(err, "Could not update custodian"), {
        variant: "error",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCustodian(id),
    onSuccess: () => {
      enqueueSnackbar("Custodian removed", { variant: "success" });
      invalidate();
    },
    onError: (err) =>
      enqueueSnackbar(apiErrorMessage(err, "Could not remove custodian"), {
        variant: "error",
      }),
  });

  const validName = form.name.trim().length > 0;
  const validEmail = EMAIL_RE.test(form.email.trim());
  const canAdd = validName && validEmail && !addMutation.isPending;

  const columns: DataTableColumn<Custodian>[] = [
    { field: "name", header: "Name", width: 180 },
    { field: "email", header: "Email" },
    {
      field: "department",
      header: "Department",
      width: 200,
      render: (r) => r.department ?? <em style={{ opacity: 0.6 }}>any</em>,
    },
    {
      field: "role",
      header: "Role",
      width: 160,
      render: (r) => (
        <Chip
          label={labelForAdminRole(r.role)}
          colorVariant={r.role === "hr" ? "primary" : "info"}
          chipVariant="outlined"
        />
      ),
    },
    {
      field: "active",
      header: "Active",
      width: 110,
      render: (r) => (
        <Switch
          checked={r.active}
          onChange={(_, v) =>
            activeMutation.mutate({ id: r.id, active: v })
          }
          size="small"
        />
      ),
    },
    {
      field: "id",
      header: "",
      width: 60,
      align: "right",
      render: (r) => (
        <IconButton
          size="small"
          onClick={() => {
            if (window.confirm(`Remove ${r.name}?`))
              deleteMutation.mutate(r.id);
          }}
        >
          <RavenIcon icon={Trash2} />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" mb={0.5}>
        Custodian recipients
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Email recipients notified when a new suggestion is submitted. Optional
        department scoping routes submissions by originating department.
      </Typography>

      <Alert severity="info" sx={{ mb: 2, maxWidth: 960 }}>
        Recipients are saved locally until the admin backend is deployed.
        Adding or removing here does not yet change who is notified by the
        server.
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" mb={1}>
            Add a recipient
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                error={form.email.length > 0 && !validEmail}
                helperText={
                  form.email.length > 0 && !validEmail
                    ? "Enter a valid email"
                    : undefined
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Select
                label="Department (optional)"
                value={form.department}
                onChange={(v) => setForm({ ...form, department: v })}
                options={[
                  { label: "All Departments", value: "" },
                  ...buildDepartmentSelectOptions(departmentList),
                ]}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Select
                label="Role"
                value={form.role}
                onChange={(v) =>
                  setForm({ ...form, role: v as "hr" | "corporate" })
                }
                options={roleOptions}
              />
            </Grid>
            <Grid size={12}>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  startIcon={<RavenIcon icon={Plus} />}
                  disabled={!canAdd}
                  onClick={() => addMutation.mutate()}
                >
                  Add recipient
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Table<Custodian>
        columns={columns}
        rows={custodians}
        rowKey="id"
        page={0}
        rowsPerPage={custodians.length || 10}
        total={custodians.length}
        onPageChange={() => undefined}
        onRowsPerPageChange={() => undefined}
        loading={isLoading}
        emptyLabel="No custodians configured yet"
      />
    </Box>
  );
}
